import React, { useState } from 'react';
import { Plus, X, RotateCcw, SlidersHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { DEFAULT_BREAKS } from './ageingUtils';

const validate = (vals) => {
  const nums = vals.map((v) => Number(v));
  if (nums.some((n) => !Number.isInteger(n) || n < 1 || n > 3650)) return 'Each range must end on a whole number of days (1–3650).';
  if (nums.some((n, i) => i > 0 && n <= nums[i - 1])) return 'Each range must end after the one above it.';
  return null;
};

export const AgeRangeEditor = ({ breaks, onApply }) => {
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState(breaks.map(String));
  const err = validate(vals);

  const openEditor = () => { setVals(breaks.map(String)); setOpen(true); };
  const setAt = (i, v) => setVals((p) => p.map((x, j) => (j === i ? v.replace(/[^0-9]/g, '') : x)));
  const addRow = () => setVals((p) => [...p, String((Number(p[p.length - 1]) || 0) + 30)]);
  const removeRow = (i) => setVals((p) => (p.length > 1 ? p.filter((_, j) => j !== i) : p));
  const apply = () => { onApply(vals.map(Number)); setOpen(false); };

  return (
    <>
      <Button variant="outline" onClick={openEditor} className="border-stone-300 bg-white" data-testid="ageing-edit-ranges-btn">
        <SlidersHorizontal className="w-4 h-4 mr-2" /> Edit age ranges
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="ageing-ranges-dialog">
          <DialogHeader>
            <DialogTitle>Age ranges (days)</DialogTitle>
            <DialogDescription>Each range includes its last day. Anything older than the last range goes into the final "&gt;" column.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {vals.map((v, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`ageing-range-row-${i}`}>
                <span className="w-16 text-right text-sm tabular-nums text-stone-500">{i === 0 ? 0 : (Number(vals[i - 1]) || 0) + 1}</span>
                <span className="text-stone-400">to</span>
                <Input value={v} onChange={(e) => setAt(i, e.target.value)} inputMode="numeric" className="w-24 tabular-nums" data-testid={`ageing-range-input-${i}`} />
                <span className="text-sm text-stone-500">days</span>
                <Button variant="ghost" size="icon" onClick={() => removeRow(i)} disabled={vals.length === 1} data-testid={`ageing-range-remove-${i}`}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <div className="flex items-center gap-2 text-sm text-stone-600 pl-2" data-testid="ageing-range-last">
              <span className="w-14 text-right tabular-nums">&gt; {vals[vals.length - 1] || '?'}</span> days (oldest)
            </div>
          </div>
          {err && <p className="text-sm text-red-600" data-testid="ageing-range-error">{err}</p>}
          <DialogFooter className="gap-2 sm:justify-between">
            <div className="flex gap-2">
              <Button variant="outline" onClick={addRow} disabled={vals.length >= 20} data-testid="ageing-range-add-btn"><Plus className="w-4 h-4 mr-1" /> Add range</Button>
              <Button variant="ghost" onClick={() => setVals(DEFAULT_BREAKS.map(String))} data-testid="ageing-range-reset-btn"><RotateCcw className="w-4 h-4 mr-1" /> Default</Button>
            </div>
            <Button onClick={apply} disabled={!!err} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="ageing-range-apply-btn">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
